import { Link } from "react-router-dom";
import { Clock, Flame } from "lucide-react";
import { useH3Promo } from "@/hooks/use-h3-promo";
import { POWER_TIER_LABEL } from "@/config/models";
import { H3_PROMO_DISCOUNT_LABEL } from "@/config/h3-promo";

/**
 * Top-of-page flash-sale strip. Shows a cool lavender teaser countdown
 * before the sale starts, a deep purple countdown while it's live, and
 * returns `null` the moment the hardcoded deadline passes — the regular
 * "Extend Video" banner takes its place automatically.
 *
 * Mobile and desktop render as two completely separate rows so mobile
 * can lead with COMING SOON (instead of hiding it behind a breakpoint)
 * — users should never see a bare "60% OFF" + timer during the teaser
 * phase and mistake it for a live sale.
 */
export function H3PromoBanner() {
  const { phase, countdownLabel } = useH3Promo();
  if (phase === "ended") return null;

  const isActive = phase === "active";
  const Icon = isActive ? Flame : Clock;
  const bgClass = isActive
    ? "bg-[#241d3a] border-b-2 border-[#0f0a1e]"
    : "bg-[#4a3e73] border-b-2 border-[#2a1f52]";
  const accentClass = isActive ? "text-[#ffd66b]" : "text-[#e3d8ff]";

  return (
    <Link
      to="/create-cartoon"
      className={`flex items-center justify-center ${bgClass} py-1.5 px-3 sm:px-4 text-white text-[11px] md:text-[13px] font-extrabold whitespace-nowrap transition-colors hover:brightness-110 uppercase tracking-wide`}
    >
      {/* Mobile layout (<md) */}
      <span className="md:hidden flex items-center justify-center gap-x-2">
        <Icon className={`size-3.5 shrink-0 ${accentClass}`} strokeWidth={3} />
        {isActive ? (
          <>
            <span>
              {H3_PROMO_DISCOUNT_LABEL} {POWER_TIER_LABEL}
            </span>
            <span className="text-white/40">·</span>
            <span className={`tabular-nums ${accentClass}`}>{countdownLabel}</span>
          </>
        ) : (
          <>
            <span>Coming Soon</span>
            <span className="text-white/40">·</span>
            <span>{H3_PROMO_DISCOUNT_LABEL}</span>
            <span className="text-white/40">·</span>
            <span className={`tabular-nums ${accentClass}`}>{countdownLabel}</span>
          </>
        )}
      </span>

      {/* Desktop layout (md+) */}
      <span className="hidden md:flex items-center justify-center gap-x-3">
        <Icon className={`size-4 shrink-0 ${accentClass}`} strokeWidth={3} />
        <span>
          {H3_PROMO_DISCOUNT_LABEL} {POWER_TIER_LABEL} ·{" "}
          {isActive ? "24-hour flash sale" : "coming soon"}
        </span>
        <span className="text-white/40">·</span>
        <span className={`tabular-nums ${accentClass}`}>
          {isActive ? "ends in" : "starts in"} {countdownLabel}
        </span>
      </span>
    </Link>
  );
}
